import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { type Crisis, type CreateCrisisData, type UpdateCrisisData } from '../types'
import { crisisService } from '../services/crisisService'

// Initialization states for clearer lifecycle tracking
type InitState = 'idle' | 'initializing' | 'ready' | 'error'

interface CrisisState {
  activeCrisis: Crisis | null
  pastCrises: Crisis[]
  isLoading: boolean
  error: string | null
  initState: InitState
}

interface CrisisActions {
  // State setters
  setActiveCrisis: (crisis: Crisis | null) => void
  setPastCrises: (crises: Crisis[]) => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  setInitState: (state: InitState) => void

  // Actions
  createCrisis: (data: CreateCrisisData) => Promise<Crisis>
  selectCrisis: (crisisId: string) => Promise<Crisis>
  updateCrisis: (crisisId: string, data: UpdateCrisisData) => Promise<Crisis>
  closeCrisis: (crisisId: string) => Promise<void>
  deleteCrisis: (crisisId: string) => Promise<void>
  loadPastCrises: () => Promise<Crisis[]>
  clearActiveCrisis: () => void
  refreshActiveCrisis: (allowSet?: boolean) => Promise<void>
  reset: () => void
}

type CrisisStore = CrisisState & CrisisActions

export const useCrisisStore = create<CrisisStore>()(
  persist(
    (set, get) => ({
      // Initial state
      activeCrisis: null,
      pastCrises: [],
      isLoading: false,
      error: null,
      initState: 'idle',

      // State setters
      setActiveCrisis: crisis => set({ activeCrisis: crisis }),
      setPastCrises: crises => set({ pastCrises: crises }),
      setLoading: loading => set({ isLoading: loading }),
      setError: error => set({ error }),
      setInitState: (state: InitState) => set({ initState: state }),

      // Actions
      createCrisis: async (data: CreateCrisisData): Promise<Crisis> => {
        console.debug('[crisisStore] createCrisis called:', data)
        set({ isLoading: true, error: null })
        try {
          const crisis = await crisisService.createCrisis(data)
          console.debug('[crisisStore] Crisis created, setting as active:', crisis)
          set({
            activeCrisis: crisis,
            isLoading: false,
            initState: 'ready',
          })
          return crisis
        } catch (error) {
          console.error('[crisisStore] createCrisis error:', error)
          const errorMessage = error instanceof Error ? error.message : 'Failed to create crisis'
          set({ error: errorMessage, isLoading: false })
          throw error
        }
      },

      selectCrisis: async (crisisId: string): Promise<Crisis> => {
        console.debug('[crisisStore] selectCrisis called:', crisisId)
        set({ isLoading: true, error: null })
        try {
          const crisis = await crisisService.selectCrisis(crisisId)
          console.debug('[crisisStore] Crisis fetched:', crisis)

          // Set as active crisis regardless of status (can be active or closed)
          // This allows users to view closed crises (read-only mode)
          console.debug('[crisisStore] Setting as active crisis (status:', crisis.status, ')')
          set({
            activeCrisis: crisis,
            isLoading: false,
            initState: 'ready',
          })

          return crisis
        } catch (error) {
          console.error('[crisisStore] selectCrisis error:', error)
          const errorMessage = error instanceof Error ? error.message : 'Failed to select crisis'
          set({ error: errorMessage, isLoading: false })
          throw error
        }
      },

      updateCrisis: async (crisisId: string, data: UpdateCrisisData): Promise<Crisis> => {
        set({ isLoading: true, error: null })
        try {
          // Update the store optimistically first
          const { activeCrisis } = get()
          if (activeCrisis?.id === crisisId) {
            set({
              activeCrisis: {
                ...activeCrisis,
                ...data,
              },
            })
          }

          // Then call the API
          const crisis = await crisisService.updateCrisis(crisisId, data)

          // Update with the server response
          if (activeCrisis?.id === crisisId) {
            set({ activeCrisis: crisis })
          }

          set({ isLoading: false })
          return crisis
        } catch (error) {
          // Revert optimistic update on error
          await get().refreshActiveCrisis()

          const errorMessage = error instanceof Error ? error.message : 'Failed to update crisis'
          set({ error: errorMessage, isLoading: false })
          throw error
        }
      },

      closeCrisis: async (crisisId: string): Promise<void> => {
        set({ isLoading: true, error: null })
        try {
          await crisisService.closeCrisis(crisisId)
          // Clear active crisis if it's the one being closed
          const { activeCrisis } = get()
          if (activeCrisis?.id === crisisId) {
            set({ activeCrisis: null })
          }
          // Refresh past crises to include the newly closed one
          await get().loadPastCrises()
          set({ isLoading: false })
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to close crisis'
          set({ error: errorMessage, isLoading: false })
          throw error
        }
      },

      deleteCrisis: async (crisisId: string): Promise<void> => {
        set({ isLoading: true, error: null })
        try {
          await crisisService.deleteCrisis(crisisId)
          const { activeCrisis } = get()
          if (activeCrisis?.id === crisisId) set({ activeCrisis: null })
          await get().loadPastCrises()
          set({ isLoading: false })
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to delete crisis'
          set({ error: errorMessage, isLoading: false })
          throw error
        }
      },

      loadPastCrises: async (): Promise<Crisis[]> => {
        console.debug('[crisisStore] loadPastCrises called')
        set({ isLoading: true, error: null })
        try {
          const crises = await crisisService.getCrises()
          console.debug('[crisisStore] Loaded past crises:', crises.length)
          set({
            pastCrises: crises,
            isLoading: false,
          })
          return crises
        } catch (error) {
          console.error('[crisisStore] loadPastCrises error:', error)
          const errorMessage = error instanceof Error ? error.message : 'Failed to load past crises'
          set({ error: errorMessage, isLoading: false })
          throw error
        }
      },

      clearActiveCrisis: () => {
        set({ activeCrisis: null })
        try {
          sessionStorage.setItem('cb:activeCrisisAutoSelected', '1')
        } catch {
          return
        }
      },

      refreshActiveCrisis: async (): Promise<void> => {
        // Centralized initialization logic with session-based auto-select
        // Only auto-selects crisis once per browser session to avoid overwriting
        // user's manual deselection
        const sessionKey = 'cb:activeCrisisAutoSelected'
        const alreadyAutoSelected =
          typeof window !== 'undefined' && !!sessionStorage.getItem(sessionKey)

        set({ isLoading: true, error: null, initState: 'initializing' })
        try {
          const crisis = await crisisService.getActiveCrisis()
          const { activeCrisis: currentCrisis } = get()

          // Only auto-set if: no current crisis + not yet auto-selected this session
          if (!currentCrisis && !alreadyAutoSelected && crisis) {
            console.debug('[crisisStore] Auto-selecting active crisis (first time this session)')
            set({
              activeCrisis: crisis,
              isLoading: false,
              initState: 'ready',
            })
            try {
              sessionStorage.setItem(sessionKey, '1')
            } catch {
              // Ignore sessionStorage errors (e.g., private browsing)
            }
            return
          }

          console.debug(
            '[crisisStore] refreshActiveCrisis: preserving user selection or already auto-selected'
          )
          set({ isLoading: false, initState: 'ready' })
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Failed to refresh active crisis'
          console.error('[crisisStore] refreshActiveCrisis error:', errorMessage)
          set({ error: errorMessage, isLoading: false, initState: 'error' })
          // Don't throw - allow app to continue without crisis
        }
      },

      reset: () => {
        set({
          activeCrisis: null,
          pastCrises: [],
          isLoading: false,
          error: null,
          initState: 'idle',
        })
      },
    }),
    {
      name: 'crisis-storage',
      partialize: state => ({
        activeCrisis: state.activeCrisis,
        pastCrises: state.pastCrises,
      }),
    }
  )
)
